import React from 'react';
import { Box } from '@mui/material';
import { getServiceLogo, hasServiceLogo } from '../utils/serviceLogos';

interface ServiceLogoProps {
  serviceName: string;
  category: string;
  size?: 'small' | 'medium' | 'large';
  showFallback?: boolean;
}

const ServiceLogo: React.FC<ServiceLogoProps> = ({
  serviceName,
  category,
  size = 'medium',
  showFallback = true,
}) => {
  const logo = getServiceLogo(serviceName, category);
  const hasLogo = hasServiceLogo(serviceName);
  
  // Size mappings
  const sizeMap = {
    small: { width: 24, height: 24, fontSize: '0.75rem' },
    medium: { width: 32, height: 32, fontSize: '1rem' },
    large: { width: 40, height: 40, fontSize: '1.25rem' },
  };
  
  const { width, height, fontSize } = sizeMap[size];
  
  // If it's an emoji (fallback), render as text
  if (!hasLogo && showFallback) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize,
          borderRadius: '4px',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          color: 'text.primary',
        }}
      >
        {logo}
      </Box>
    );
  }
  
  // If it's an image logo, render as Box with image
  if (hasLogo) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: '4px',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={logo}
          alt={`${serviceName} logo`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={(e) => {
            // Fallback to text if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = serviceName.charAt(0).toUpperCase();
              parent.style.fontSize = '0.6rem';
              parent.style.color = 'inherit';
            }
          }}
        />
      </Box>
    );
  }
  
  // Fallback to category icon
  return (
    <Box
      sx={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        borderRadius: '4px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: 'text.primary',
      }}
    >
      {logo}
    </Box>
  );
};

export default ServiceLogo;
